import type { AxiosError } from "axios";
import axios from "axios";
import type { Dispatch } from "react";
import { useTranslation } from "react-i18next";
import type { TemplateExportDocument } from "../../../../../../components/DownloadTemplateFiles/DownloadTemplateFiles";
import { TemplateFilesDownloadPanel } from "../../../../../../components/DownloadTemplateFiles/DownloadTemplateFiles";
import { endpoints } from "../../../../../../endpoints";
import type { OptionType } from "../../../../../../types/types";
import type { ProductExportStatusSchema } from "../../../../../../types/types.PIM";
import { useStoreState } from "../../../../../../util/util";
import { useAuthContext } from "../../../../../../components/Auth";
import type {
  UploadAction,
  UploadState,
} from "./createProductFromUploads.util";
import { DEFAULT_STATUSES } from "./createProductFromUploads.util";
import { CreateProductsList } from "./CreateProductsList";

export const DownloadProductTemplateFiles = ({
  template,
  dispatch,
  export_id,
  selected_products,
}: {
  template: OptionType<string>;
  dispatch: Dispatch<UploadAction>;
  export_id?: string;
  selected_products: UploadState["selected_products"];
}) => {
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();
  const { hasPermission } = useAuthContext();

  const hasEditOrPublishPermission =
    hasPermission("modify_products") || hasPermission("publish_products");

  return (
    <TemplateFilesDownloadPanel
      template={template}
      tenantId={tenant_id}
      exportId={export_id}
      defaultSelectedItems={selected_products}
      copy={{
        description: t(
          "The following CSV files contain product data for all groups & collections used in the selected template. Download these files to update existing product data or to create new products."
        ),
        reDownloadNote: t(
          "Please do not modify the first rows containing attribute details. We also recommend keeping the same file name."
        ),
        includeExistingDataLabel: t("Include existing product data"),
        groupsSectionNote: t(
          "This file is required to create product profiles"
        ),
        collectionsSectionNote: t(
          "These files are used to update existing products with collection data"
        ),
        preparingFilesMessage: t(
          "Please wait, your product data is being prepared for download..."
        ),
        pollFailedMessage: t(
          "Could not fetch existing product data. Please try again later."
        ),
        genericExportError: t("Could not fetch existing product data."),
      }}
      onSetExportId={(next_export_id) =>
        dispatch({
          type: "set_export_id",
          payload: { export_id: next_export_id } as UploadState,
        })
      }
      onSetSelectedItems={(selected_products) =>
        dispatch({
          type: "set_selected_products",
          payload: { selected_products } as UploadState,
        })
      }
      onMoveBack={() =>
        dispatch({
          type: "move_back",
          payload: { uploadStage: "download" } as UploadState,
        })
      }
      onContinueFromDownload={(collectionFileNames) =>
        dispatch({
          type: "continue_from_download",
          payload: { collectionFileNames } as UploadState,
        })
      }
      startExport={async (selected_items) => {
        const params = new URLSearchParams({
          template: template.label,
          show_inactive: hasEditOrPublishPermission ? "true" : "false",
          ...(!hasEditOrPublishPermission && {
            published_products_only: "true",
          }),
        });
        DEFAULT_STATUSES.forEach((status) => params.append("status", status));
        params.append("limit", "100000000");
        if (selected_items !== "all" && selected_items.length) {
          selected_items.forEach((product_id) =>
            params.append("product_id", product_id)
          );
        }
        const { data } = await axios.post<ProductExportStatusSchema>(
          endpoints.v2_storefronts_id_pim_products_export_async(storefront_id),
          {},
          { params }
        );
        return { id: data.id };
      }}
      getNoItemsErrorMessage={(error: AxiosError) => {
        const data = error?.response?.data;
        if (
          data?.status_code === "404" &&
          data?.message === "No products found"
        ) {
          return t("There are no existing products for this template");
        }
        return undefined;
      }}
      pollExportStatus={async (
        job_id
      ): Promise<{
        status: ProductExportStatusSchema["status"];
        documents: TemplateExportDocument[];
      }> => {
        const {
          data: { status, documents },
        } = await axios.get<ProductExportStatusSchema>(
          endpoints.v2_storefronts_id_pim_products_export_job_id(
            storefront_id,
            job_id
          )
        );
        return {
          status,
          documents: documents.map(({ signed_url, name, id }) => ({
            id,
            name,
            url: signed_url,
          })),
        };
      }}
      renderSpecificItemsSelector={({
        handleSelectRows,
        selectedTemplate,
        defaultSelectedRows,
      }) => (
        <CreateProductsList
          handleSelectRows={handleSelectRows}
          selectedTemplate={selectedTemplate}
          defaultSelectedRows={defaultSelectedRows}
        />
      )}
    />
  );
};
